import { z } from 'zod'
import { dateOrIsoString } from '~/prix/types/date'
import { enumerated } from '~/prix/types/enumerated'
import sql from '~/prix/utils/sql'
import { creditNumericValue } from '../credit/creditNumericValue'
import { creditProgramObject } from '../creditProgram/creditProgramObject'
import { citizenObject } from '~/packages/general/citizen/citizenObject'
import { residenceObject } from '~/packages/general/residence/residenceObject'

export const creditRequestStatus = enumerated({
  draft: 'Rascunho',
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  paid: 'Pago',
  cancelled: 'Cancelado',
})

export const creditRequestObject = z.object({
  id: z.string().uuid(),
  code: z.number().int(),
  creditProgramId: creditProgramObject.shape.id,
  citizenId: citizenObject.shape.id,
  residenceId: residenceObject.shape.id,
  requestDate: dateOrIsoString(),
  totalMovementValue: creditNumericValue,
  estimatedBonusValue: creditNumericValue,
  status: creditRequestStatus,
  bankNumber: z.string().nullish(),
  bankName: z.string().nullish(),
  bankBranchName: z.string().nullish(),
  bankBranchNumber: z.string().nullish(),
  bankAccountNumber: z.string().nullish(),
  bankAccountHolderName: z.string().nullish(),
  pixKey: z.string().nullish(),
  sourceFilter: z.string().nullish(),
  clientId: z.number().int(),
  createdAt: dateOrIsoString().nullish(),
  updatedAt: dateOrIsoString().nullish(),
  deletedAt: dateOrIsoString().nullish(),
})

export const creditRequestCreateInputObject = creditRequestObject
  .pick({
    id: true,
    creditProgramId: true,
    citizenId: true,
    residenceId: true,
    requestDate: true,
    totalMovementValue: true,
    estimatedBonusValue: true,
    status: true,
    bankNumber: true,
    bankName: true,
    bankBranchName: true,
    bankBranchNumber: true,
    bankAccountNumber: true,
    bankAccountHolderName: true,
    pixKey: true,
    clientId: true,
  })
  .extend({
    id: creditRequestObject.shape.id.nullish(),
    requestDate: creditRequestObject.shape.requestDate.nullish(),
    totalMovementValue: creditRequestObject.shape.totalMovementValue.nullish(),
    estimatedBonusValue: creditRequestObject.shape.estimatedBonusValue.nullish(),
    status: creditRequestObject.shape.status.nullish(),
    clientId: creditRequestObject.shape.clientId.nullish(),
  })

export const creditRequestUpdateInputObject = creditRequestObject
  .pick({
    id: true,
    creditProgramId: true,
    citizenId: true,
    residenceId: true,
    requestDate: true,
    totalMovementValue: true,
    estimatedBonusValue: true,
    status: true,
    bankNumber: true,
    bankName: true,
    bankBranchName: true,
    bankBranchNumber: true,
    bankAccountNumber: true,
    bankAccountHolderName: true,
    pixKey: true,
  })
  .partial()
  .required({ id: true })

export type CreditRequestType = z.infer<typeof creditRequestObject>

export const propertyMap = {
  id: sql`credit_request.id`,
  code: sql`credit_request.code`,
  creditProgramId: sql`credit_request.credit_program_id`,
  citizenId: sql`credit_request.citizen_id`,
  residenceId: sql`credit_request.residence_id`,
  requestDate: sql`credit_request.request_date`,
  totalMovementValue: sql`credit_request.total_movement_value`,
  estimatedBonusValue: sql`credit_request.estimated_bonus_value`,
  status: sql`credit_request.status`,
  bankNumber: sql`credit_request.bank_number`,
  bankName: sql`credit_request.bank_name`,
  bankBranchName: sql`credit_request.bank_branch_name`,
  bankBranchNumber: sql`credit_request.bank_branch_number`,
  bankAccountNumber: sql`credit_request.bank_account_number`,
  bankAccountHolderName: sql`credit_request.bank_account_holder_name`,
  pixKey: sql`credit_request.pix_key`,
  sourceFilter: sql`credit_request.source_filter`,
  clientId: sql`credit_request.client_id`,
  createdAt: sql`credit_request.created_at`,
  updatedAt: sql`credit_request.updated_at`,
  deletedAt: sql`credit_request.deleted_at`,
}

export default creditRequestObject
